import { AccountingTaxCode } from '../accounting-tax-code.model';
import { calculateTaxAmount } from './accounting-line-tax';
import { roundCurrency } from '../../shared/utils/commercial-money';

export interface RatedEditorLine {
    quantity: number;
    unitRate: number | null;
    rateEntered: boolean;
    recalculateTax: boolean;
    amountExclTax: number;
    taxAmount: number;
    taxCode: string;
    productId?: number;
    productCode?: string;
    incomeAccountId?: number | null;
    expenseAccountId?: number | null;
    savedTaxCode?: string | null;
}

export function rateSnapshot(quantity: number, amount: number, storedRate?: number | null): Pick<RatedEditorLine, 'unitRate' | 'rateEntered' | 'recalculateTax'> {
    return { unitRate: storedRate ?? (quantity > 0 ? Math.round(amount / quantity * 100_000_000) / 100_000_000 : null), rateEntered: storedRate != null, recalculateTax: false };
}

export function recalculateRatedLine(line: RatedEditorLine, taxCodes: AccountingTaxCode[], currencyCode = 'SAR'): void {
    line.rateEntered = true;
    line.recalculateTax = true;
    if (line.unitRate === null || !Number.isFinite(line.quantity) || !Number.isFinite(line.unitRate)) return;
    line.amountExclTax = roundCurrency(Math.max(0, line.quantity) * Math.max(0, line.unitRate), currencyCode);
    line.taxAmount = calculateTaxAmount(line.amountExclTax, line.taxCode, taxCodes, currencyCode);
}

export function validRatedLine(line: RatedEditorLine): boolean {
    return Number.isFinite(line.quantity) && line.quantity > 0 && line.unitRate !== null
        && Number.isFinite(line.unitRate) && line.unitRate >= 0 && line.amountExclTax > 0;
}

/** Resolve a missing legacy code without changing the frozen tax amount. */
export function restoreTaxCodes(lines: RatedEditorLine[], taxCodes: AccountingTaxCode[]): AccountingTaxCode[] {
    const options = [...taxCodes];
    for (const line of lines) {
        if (!line.savedTaxCode && !line.recalculateTax) {
            const matching = taxCodes.find(code => calculateTaxAmount(line.amountExclTax, code.taxCode, taxCodes) === line.taxAmount);
            if (matching) line.taxCode = matching.taxCode;
        }
        if (line.taxCode && !options.some(code => code.taxCode === line.taxCode)) options.push({
            taxCode: line.taxCode, description: line.taxCode,
            taxRate: line.amountExclTax > 0 ? line.taxAmount * 100 / line.amountExclTax : 0
        });
    }
    return options;
}

export function pickExcelValue(row: Record<string, unknown>, keys: string[]): unknown {
    const normalized = Object.entries(row).reduce<Record<string, unknown>>((map, [key, value]) => {
        map[key.replace(/\s|_/g, '').toLowerCase()] = value; return map;
    }, {});
    return keys.map(key => normalized[key.replace(/\s|_/g, '').toLowerCase()]).find(value => value !== undefined && value !== null && value !== '');
}
